import memoize from 'lodash/memoize';

import FeatureGates from '@atlaskit/feature-gate-js-client';
import {
	getFrontendExperiment,
	type ServedExperiment,
	STATSIG_SEARCH_BACKEND_NON_OVERLAPPING_DOMAIN,
} from '@atlassian/search-experiment';

import { logger } from './logger';

export const SAIN_LAYER_NAME = 'kd_sain_layer';
export const SAIN_HELLO_LAYER_NAME = 'kd_sain_hello_layer';

// exposed for testing
export function isPartofSainHoldbackExperiment(): boolean {
	const layer = FeatureGates.getLayer(STATSIG_SEARCH_BACKEND_NON_OVERLAPPING_DOMAIN, {
		fireLayerExposure: false,
	});
	const abTestId = layer.get<string | null>('abTestId', 'no_ab_test_id');
	if (abTestId === 'kd_disable_sain_experiment') {
		const backendExperimentData = layer.get<{ sain: boolean } | null>('backendChanges', {
			sain: true,
		});
		if (!backendExperimentData?.sain) {
			return true;
		}
	}
	return false;
}

function doesLayerFrontendExperimentExist(layerName: string, abTestId: string): boolean {
	const layer = FeatureGates.getLayer(layerName, {
		fireLayerExposure: false,
	});
	const layerFrontendExperimentName = layer.get('frontendExperimentName', null);
	return layerFrontendExperimentName !== null && layerFrontendExperimentName === abTestId;
}

function getLayerFrontendExperimentUnmemoized({
	layerName,
	abTestId,
	onError,
}: {
	layerName: string;
	abTestId: string;
	onError?: (error: Error) => void;
}): ServedExperiment {
	const falseEvaluationResult = { serveExperiment: false, abTestId, cohort: 'not-enrolled' };
	const handleError = (error: Error) => {
		logger.error({ error, functionName: `getLayerFrontendExperimentUnmemoized(${layerName})` });
		onError?.(error);
	};

	try {
		// we skip experiment evaluation if the user is part of the SAIN holdback experiment test cohort,
		// because SAIN is disabled in this case
		if (isPartofSainHoldbackExperiment()) {
			return falseEvaluationResult;
		}
		if (doesLayerFrontendExperimentExist(layerName, abTestId)) {
			return getFrontendExperiment({ abTestId, onError: handleError });
		}
	} catch (error) {
		handleError(error as Error);
	}

	return falseEvaluationResult;
}

export const getSAINLayerFrontendExperiment = memoize(
	(params: { abTestId: string; onError?: (error: Error) => void }) =>
		getLayerFrontendExperimentUnmemoized({ ...params, layerName: SAIN_LAYER_NAME }),
	({ abTestId }) => abTestId,
);

export const getSAINHelloLayerFrontendExperiment = memoize(
	(params: { abTestId: string; onError?: (error: Error) => void }) =>
		getLayerFrontendExperimentUnmemoized({ ...params, layerName: SAIN_HELLO_LAYER_NAME }),
	({ abTestId }) => abTestId,
);

type ServedMultiArmExperiment = {
	abTestId: string;
	cohort: string;
};

function getLayerFrontendMultiArmExperimentUnmemoized({
	layerName,
	abTestId,
	onError,
}: {
	layerName: string;
	abTestId: string;
	onError?: (error: Error) => void;
}): ServedMultiArmExperiment {
	const handleError = (error: Error) => {
		logger.error({ error, functionName: `getLayerFrontendExperimentUnmemoized(${layerName})` });
		onError?.(error);
	};

	try {
		// we skip experiment evaluation if the user is part of the SAIN holdback experiment test cohort,
		// because SAIN is disabled in this case
		if (
			!isPartofSainHoldbackExperiment() &&
			doesLayerFrontendExperimentExist(layerName, abTestId)
		) {
			const experiment = FeatureGates.getExperiment(abTestId);
			const cohort = experiment.get('cohort', 'not-enrolled');
			return {
				abTestId,
				cohort,
			};
		}
	} catch (error) {
		handleError(error as Error);
	}

	return { abTestId, cohort: 'not-enrolled' };
}

export const getSAINLayerFrontendMutiArmExperiment = memoize(
	(params: { abTestId: string; onError?: (error: Error) => void }) =>
		getLayerFrontendMultiArmExperimentUnmemoized({ ...params, layerName: SAIN_LAYER_NAME }),
	({ abTestId }) => abTestId,
);

export const getSAINHelloLayerFrontendMutiArmExperiment = memoize(
	(params: { abTestId: string; onError?: (error: Error) => void }) =>
		getLayerFrontendMultiArmExperimentUnmemoized({ ...params, layerName: SAIN_HELLO_LAYER_NAME }),
	({ abTestId }) => abTestId,
);

export const getSAINLayerParamValue = memoize(
	(paramName: string) => {
		const layer = FeatureGates.getLayer(SAIN_LAYER_NAME);
		return layer.get(paramName, null);
	},
	(paramName) => paramName,
);

export const hasSAINLayerParamValue = (paramName: string, paramValue: string) => {
	return getSAINLayerParamValue(paramName) === paramValue;
};

export const getSAINHelloLayerParamValue = memoize(
	(paramName: string) => {
		const layer = FeatureGates.getLayer(SAIN_HELLO_LAYER_NAME);
		return layer.get(paramName, null);
	},
	(paramName) => paramName,
);

export const hasSAINHelloLayerParamValue = (paramName: string, paramValue: string) => {
	return getSAINHelloLayerParamValue(paramName) === paramValue;
};
